import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toPastTenseAction } from '../../../utils/actionLabel'
import {
  Button,
  message,
  Modal,
  Spin,
  Tabs,
} from '@surecompanies/core_components'
import EditOutlined from '@ant-design/icons/EditOutlined'
import PlanSmokerRateOverrideForm, {
  type SmokerRateServerFieldError,
} from './PlanSmokerRateOverrideForm'
import OverrideHeaderChips from '../../../components/OverrideHeaderChips'
import OverrideAuditStrip from '../../../components/OverrideAuditStrip'
import { apiPayloadToForm } from './planSmokerRateOverrideForm.schema'
import type {
  CreatePlanSmokerRateOverrideRuleRequestDto,
  UpdatePlanSmokerRateOverrideRuleRequestDto,
} from '../../../api/dto/plan-smoker-rate-override-rules.dto'
import { PlanSmokerRateOverrideRuleStatus } from '../enums/plan-smoker-rate-override-rule.enum'
import { useGetSmokerRateOverrideRule } from '../hooks/useGetSmokerRateOverrideRule'
import { useToggleSmokerRateOverrideRuleStatus } from '../hooks/useToggleSmokerRateOverrideRuleStatus'
import { useUpdateSmokerRateOverrideRule } from '../hooks/useUpdateSmokerRateOverrideRule'
import {
  extractFieldErrors,
  getApiErrorMessage,
  getApiErrorStatus,
} from '../../../utils/apiErrors'
import DocumentSection from '../../../components/DocumentSection'
import OverrideHistoryTimeline from '../../../components/OverrideHistoryTimeline'
import { useEditDocumentStaging } from '../../../hooks/useEditDocumentStaging'
import { SMOKER_RATE_OVERRIDE_RULE_BASE } from '../../../api/plan-smoker-rate-override-rules-api'
import type { SmokerRateViewMetadata } from './planSmokerRateOverrideForm.schema'
import { formatSmokerRateUpsertRule } from './rule-templates'

export type PlanSmokerRateOverrideModalMode = 'view' | 'edit'

interface Props {
  ruleId: string | null
  initialMode?: PlanSmokerRateOverrideModalMode
  onClose: () => void
  onModeChange?: (mode: PlanSmokerRateOverrideModalMode) => void
  onSaved?: (status: PlanSmokerRateOverrideRuleStatus) => void
  canEdit?: boolean
}

export default function PlanSmokerRateOverrideModal({
  ruleId,
  initialMode = 'view',
  onClose,
  onModeChange,
  onSaved,
  canEdit = true,
}: Props) {
  const open = ruleId !== null
  const [mode, setMode] = useState<PlanSmokerRateOverrideModalMode>(initialMode)
  const [activeTab, setActiveTab] = useState('details')
  const [serverErrors, setServerErrors] = useState<
    SmokerRateServerFieldError[]
  >([])
  const {
    data: rule,
    isLoading,
    error,
  } = useGetSmokerRateOverrideRule(ruleId ?? undefined)
  const { mutateAsync, isPending: isUpdatePending } =
    useUpdateSmokerRateOverrideRule(ruleId ?? '')
  const { mutateAsync: toggleStatus, isPending: isTogglePending } =
    useToggleSmokerRateOverrideRuleStatus()
  const isPending = isUpdatePending || isTogglePending
  const [isSubmitLocked, setIsSubmitLocked] = useState(false)
  const submitLockRef = useRef(false)
  const staging = useEditDocumentStaging({
    ruleId: ruleId ?? '',
    ruleBase: SMOKER_RATE_OVERRIDE_RULE_BASE,
  })
  const isBusy = isPending || staging.isCommitting || isSubmitLocked

  useEffect(() => {
    if (open) {
      setMode(initialMode)
      setActiveTab('details')
      setServerErrors([])
    }
  }, [open, initialMode, ruleId])

  const stagingDiscard = staging.discard
  useEffect(() => {
    stagingDiscard()
  }, [ruleId, stagingDiscard])

  const defaultValues = useMemo(() => {
    if (!rule) return undefined
    return apiPayloadToForm(rule)
  }, [rule])

  const metadata = useMemo<SmokerRateViewMetadata | undefined>(() => {
    if (!rule) return undefined
    return {
      status: rule.status,
      createdAt: rule.createdAt,
      createdByEmail: rule.createdByEmail,
      updatedAt: rule.updatedAt,
      updatedByEmail: rule.updatedByEmail,
    }
  }, [rule])

  const closeModal = () => {
    if (isBusy) return
    if (staging.hasStagedChanges) {
      Modal.confirm({
        title: 'Discard unsaved document changes?',
        content:
          'You have pending uploads or removals that have not been saved. Discard them?',
        okText: 'Discard',
        okButtonProps: { danger: true },
        cancelText: 'Keep editing',
        onOk: () => {
          staging.discard()
          setServerErrors([])
          onClose()
        },
      })
      return
    }
    setServerErrors([])
    onClose()
  }

  const handleSwitchToEdit = () => {
    setServerErrors([])
    setMode('edit')
    onModeChange?.('edit')
  }

  const handleCancelEdit = closeModal

  const handleStatusChange = (toStatus: PlanSmokerRateOverrideRuleStatus) => {
    const actionLabel =
      toStatus === PlanSmokerRateOverrideRuleStatus.APPROVED
        ? 'Approve'
        : toStatus === PlanSmokerRateOverrideRuleStatus.REJECTED
          ? 'Reject'
          : 'Re-draft'
    Modal.confirm({
      title: `${actionLabel} override`,
      content: `Are you sure you want to ${actionLabel.toLowerCase()} this override?`,
      okText: actionLabel,
      okButtonProps: {
        danger: toStatus === PlanSmokerRateOverrideRuleStatus.REJECTED,
      },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await toggleStatus({ id: ruleId ?? '', status: toStatus })
          message.success(`Override ${toPastTenseAction(actionLabel)}.`)
          if (rule) onSaved?.(toStatus)
          onClose()
        } catch {
          message.error(
            `Failed to ${actionLabel.toLowerCase()} override. Please try again.`
          )
        }
      },
    })
  }

  const handleApprove = () =>
    handleStatusChange(PlanSmokerRateOverrideRuleStatus.APPROVED)
  const handleReject = () =>
    handleStatusChange(PlanSmokerRateOverrideRuleStatus.REJECTED)
  const handleRedraft = () =>
    handleStatusChange(PlanSmokerRateOverrideRuleStatus.DRAFT)

  const handleSubmit = async (
    body: CreatePlanSmokerRateOverrideRuleRequestDto
  ) => {
    if (submitLockRef.current || isBusy) return
    submitLockRef.current = true
    setIsSubmitLocked(true)
    setServerErrors([])
    const updateBody: UpdatePlanSmokerRateOverrideRuleRequestDto = {
      params: body.params,
      sourceUrl: body.sourceUrl ?? null,
      notes: body.notes ?? null,
    }
    try {
      await mutateAsync(updateBody)
      try {
        await staging.commit()
      } catch (commitErr) {
        // eslint-disable-next-line no-console
        console.error('staged document commit failed', commitErr)
        message.error(
          'Override saved, but some document changes failed. Please retry.'
        )
        return
      }
      message.success('Override updated.')
      if (rule) onSaved?.(rule.status)
      onClose()
    } catch (err) {
      const status = getApiErrorStatus(err)
      if (status === 400 || status === 422) {
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors.length > 0) {
          setServerErrors(fieldErrors)
          return
        }
        message.error(
          getApiErrorMessage(
            'Some fields look incorrect. Please review and try again.'
          )
        )
      } else {
        // eslint-disable-next-line no-console
        console.error('updateSmokerRateOverrideRule failed', err)
        message.error('An error occurred. Please try again.')
      }
    } finally {
      submitLockRef.current = false
      setIsSubmitLocked(false)
    }
  }

  const isViewMode = mode === 'view'
  const isApproved = rule?.status === PlanSmokerRateOverrideRuleStatus.APPROVED
  const isDraft = rule?.status === PlanSmokerRateOverrideRuleStatus.DRAFT

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className="bodyPlaceholder">
        <Spin />
      </div>
    )
  } else if (error || !rule) {
    body = (
      <div className="bodyPlaceholder">
        <p>Override not found.</p>
      </div>
    )
  } else if (isViewMode) {
    body = (
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'details',
            label: 'Details',
            children: (
              <PlanSmokerRateOverrideForm
                mode="view"
                defaultValues={defaultValues}
                extraContent={
                  ruleId ? (
                    <DocumentSection
                      ruleId={ruleId}
                      readOnly
                      ruleBase={SMOKER_RATE_OVERRIDE_RULE_BASE}
                    />
                  ) : null
                }
              />
            ),
          },
          {
            key: 'history',
            label: 'History',
            children: ruleId ? (
              <OverrideHistoryTimeline
                ruleId={ruleId}
                ruleBase={SMOKER_RATE_OVERRIDE_RULE_BASE}
                staticTemplateLabel="Update smoker rate data"
                paramsFormatter={formatSmokerRateUpsertRule}
              />
            ) : null,
          },
        ]}
      />
    )
  } else {
    body = (
      <PlanSmokerRateOverrideForm
        mode="edit"
        defaultValues={defaultValues}
        metadata={metadata}
        serverErrors={serverErrors}
        isSubmitting={isBusy}
        onSubmit={handleSubmit}
        onCancel={handleCancelEdit}
        extraContent={
          ruleId ? (
            <DocumentSection
              ruleId={ruleId}
              staging={staging}
              ruleBase={SMOKER_RATE_OVERRIDE_RULE_BASE}
            />
          ) : null
        }
      />
    )
  }

  const titleNode = (
    <div className="titleRow">
      <span className="titleLeft">
        <span className="titleText">
          {isViewMode
            ? 'Review Smoker Rate Override'
            : 'Edit Smoker Rate Override'}
        </span>
        {rule && (
          <OverrideHeaderChips label="Smoker Rates" status={rule.status} />
        )}
      </span>
    </div>
  )

  return (
    <Modal
      title={titleNode}
      open={open}
      onCancel={closeModal}
      width={960}
      destroyOnClose
      maskClosable={!isBusy}
      footer={
        isViewMode && rule && activeTab === 'details' ? (
          <div className="footerRow">
            {metadata && <OverrideAuditStrip metadata={metadata} />}
            <div className="footerActions">
              <Button type="default" onClick={closeModal} disabled={isBusy}>
                Close
              </Button>
              {canEdit && (
                <Button
                  type="default"
                  onClick={handleSwitchToEdit}
                  disabled={isBusy}
                >
                  <EditOutlined /> Edit
                </Button>
              )}
              {canEdit && (
                <>
                  {isApproved && (
                    <Button danger onClick={handleReject} loading={isPending}>
                      Reject
                    </Button>
                  )}
                  {!isApproved && (
                    <Button
                      type="primary"
                      onClick={handleApprove}
                      loading={isPending}
                    >
                      Approve
                    </Button>
                  )}
                  {!isDraft && (
                    <Button onClick={handleRedraft} loading={isPending}>
                      Re-draft
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : null
      }
    >
      {body}
    </Modal>
  )
}
